import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ReportStepper from "../components/reportStepper";
import ReportFooter from "../components/reportFooter";
import SummaryTable from "../components/summaryTable";
import SuccessActions from "../components/successActions";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate, useParams } from "react-router-dom";
import useReport from "../hooks/useReport";
import {
  ReportStatus,
  ReportType,
  UpdateReportPayload,
  InternalReportStatusEnum,
} from "../api/models/report.models";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import LightReportForm from "../components/lightReportForm";
import BasicSupForm from "../components/basicSupForm";
import { useMonths } from "../hooks/useMonths";
import GoBackLink from "../components/goBackLink";

const PaddedContainer = styled.div`
  text-align: left;
  flex-grow: 1;
`;

const Description = styled.p`
  font-size: 1rem;
  max-width: 700px;
  padding-left: 2rem;
  white-space: pre-line;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
`;

const LightReport: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [reportComment, setReportComment] = useState("");
  const [forceComment, setForceComment] = useState(false);
  const [isEmptyReport, setIsEmptyReport] = useState(false);

  const [
    companyId,
    organizationId,
    companyName,
    organizationName,
    selectedYearForNewReport,
    selectedMonthForNewReport,
    selectedQuarterForNewReport,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.organizationId,
      state.companyName,
      state.organizationName,
      state.selectedYearForNewReport,
      state.selectedMonthForNewReport,
      state.selectedQuarterForNewReport,
    ]),
  );

  const { months } = useMonths();

  const [activeStep, setActiveStep] = useState(0);

  const {
    reportData,
    isLoadingReport,
    updateExistingReport,
    isUpdatingReport,
  } = useReport({
    reportId: parseInt(reportId as string, 10),
  });

  // If report is already sent, navigate to the report view page, editing is not allowed
  useEffect(() => {
    if (
      reportData &&
      reportData.reportStatus === ReportStatus.Sent &&
      activeStep !== 3
    ) {
      navigate(`/view-report/${reportId}`);
    }
  }, [reportData]);

  const handleNextStep = async () => {
    if (activeStep === 0) {
      confirmDialog({
        showHeader: false,
        message: t("message-have-you-confirmed-your-data"),
        acceptLabel: t("cmd-continue"),
        rejectLabel: t("cmd-go-back"),
        accept: () => setActiveStep((prevStep) => prevStep + 1),
        style: { minWidth: "300px" },
      });
    }

    if (activeStep === 1) {
      /* alert("Sup step not implemented yet"); */
      setActiveStep((prevStep) => prevStep + 1);
    }

    if (activeStep === 2) {
      let payload: UpdateReportPayload;
      try {
        payload = {
          id: parseInt(reportId as string, 10),
          companyId: companyId as number,
          organizationId: organizationId || undefined,
          year: reportData?.year as number,
          quarter: reportData?.quarter as number,
          month: reportData?.month as number,
          reportStatus: organizationId
            ? ReportStatus.WaitingForApproval
            : ReportStatus.Sent,
          comment: reportComment || undefined,
          reportType: reportData?.reportType || ReportType.LIGHT,
          rows: reportData?.rows || [],
          internalReportStatus: isEmptyReport
            ? InternalReportStatusEnum.ToBeReviewed
            : undefined,
        };
      } catch (error) {
        console.error("Error creating payload:", error);
        return;
      }
      await updateExistingReport(payload);
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const getNextStepLabel = () => {
    switch (activeStep) {
      case 0:
        return t("cmd-continue");
      case 1:
        return t("cmd-continue");
      case 2:
        return t("cmd-send");
      default:
        return "";
    }
  };

  const getContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <LightReportForm
            reportId={reportId ? parseInt(reportId as string, 10) : undefined}
            reportData={reportData}
            isLoadingReport={isLoadingReport}
            handleNextStep={handleNextStep}
          />
        );
      case 1:
        return (
          <BasicSupForm
            reportId={reportId ? parseInt(reportId as string, 10) : undefined}
            reportData={reportData}
            isLoadingReport={isLoadingReport}
            handleNextStep={handleNextStep}
          />
        );
      case 2:
        return (
          <SummaryTable
            reportComment={reportComment}
            setReportComment={setReportComment}
            forceComment={forceComment}
            setForceComment={setForceComment}
            setIsEmptyReport={setIsEmptyReport}
          />
        );
      case 3:
        return <SuccessActions reportId={parseInt(reportId as string, 10)} />;
      default:
        return null;
    }
  };

  const getDescription = () => {
    switch (activeStep) {
      case 0:
        return t("description-creating-light-report");
      case 1:
        return t("description-sup-info");
      case 2:
        return t("description-summary");
      case 3:
        return t("description-report-sent-successfully");
      default:
        return "";
    }
  };

  const getViewActions = () => {
    switch (activeStep) {
      case 0:
        return null;
      case 1:
        return null;
      default:
        return null;
    }
  };

  const getViewHeader = () => {
    switch (activeStep) {
      case 0:
        return t("header-create-report");
      case 1:
        return t("header-sup-info");
      case 2:
        return t("header-summary");
      case 3:
        return t("header-report-complete");
      default:
        return t("header-create-report");
    }
  };

  const getReportInfo = () => {
    if (reportData) {
      if (reportData.month) {
        return months[reportData.month - 1] + " " + reportData.year;
      }
      return `${reportData.year}${
        reportData.quarter ? ` Q${reportData.quarter}` : ""
      }`;
    } else {
      if (selectedYearForNewReport) {
        if (selectedMonthForNewReport) {
          return (
            months[selectedMonthForNewReport - 1] +
            " " +
            selectedYearForNewReport
          );
        }
        return `${selectedYearForNewReport}${
          selectedQuarterForNewReport ? ` Q${selectedQuarterForNewReport}` : ""
        }`;
      }
    }
    return "";
  };

  const getCompanyOrOrganizationName = () => {
    return organizationName || companyName || "";
  };

  return (
    <ReportContainer className="report-container">
      <ReportStepper
        activeStep={activeStep}
        steps={[
          t("label-fill-basic-information"),
          t("label-fill-sup-information"),
          t("label-summary"),
          t("label-complete"),
        ]}
      />
      <PaddedContainer>
        {getViewActions()}
        <h1 style={{ padding: "4rem 2rem 0 2rem" }}>{getViewHeader()}</h1>
        <h4
          style={{
            padding: "0 2rem 2rem 2rem",
            fontWeight: "normal",
            marginBottom: 0,
          }}
        >
          {getReportInfo() + " - " + getCompanyOrOrganizationName()}
        </h4>

        <Description>{getDescription()}</Description>

        {activeStep > 0 && activeStep < 3 && (
          <GoBackLink
            onClick={() => {
              setActiveStep((prevStep) => prevStep - 1);
            }}
          >
            {t("go-back") || "Go back"}
          </GoBackLink>
        )}

        {getContent()}
      </PaddedContainer>
      {activeStep === 2 && (
        <ReportFooter
          handleNextStep={handleNextStep}
          nextStepLabel={getNextStepLabel()}
          showConfirmationCheckbox={false}
          proceedDisabled={!reportData || reportData.rows.length === 0}
          isLoading={isUpdatingReport || isLoadingReport}
          disableNextButton={forceComment && reportComment.trim() === ""}
        />
      )}
    </ReportContainer>
  );
};

export default LightReport;
